import { getCheckoutIntent } from "@/api/rest/checkoutApi";
import { useAppDispatch } from "@/redux/hooks";
import { getActiveCarts } from "@/redux/reducers/checkout/thunk";
import { useTrackActions } from "@/utils/hooks/useTracking";
import { AnalyticsTrackingEvent } from "@brandclub/common-ui";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ShoppingCartItem } from "./types";

export const addToBagParams = {
  retailerId: "retailerId",
  retailerSku: "retailerSku",
  stacklineSku: "stacklineSku",
  quantity: "quantity",
};

export const useCreateCheckoutIntentFromQueryParams = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const processingRef = useRef(false);
  const [trackAction] = useTrackActions();
  const [processing, setProcessing] = useState(true);

  useEffect(() => {
    const addItemToBag = async () => {
      // Check if we have any relevant parameters to process
      const retailerId = searchParams.get(addToBagParams.retailerId);
      const retailerSku = searchParams.get(addToBagParams.retailerSku);
      const stacklineSku = searchParams.get(addToBagParams.stacklineSku);

      // If no relevant parameters exist, don't process anything
      if (!retailerId && !retailerSku && !stacklineSku) {
        setProcessing(false);
        return;
      }

      // Prevent multiple processing attempts
      if (processingRef.current) {
        setProcessing(false);
        return;
      }

      const quantity = parseInt(
        searchParams.get(addToBagParams.quantity) || "1",
        10
      );

      // Check if we have valid parameters based on the SKU type
      if (retailerSku && !retailerId) {
        console.error("retailerId is required when using retailerSku");
        return;
      }

      if (!retailerSku && !stacklineSku) {
        console.error("Either retailerSku or stacklineSku is required");
        return;
      }

      try {
        processingRef.current = true;
        setProcessing(true);

        // Prepare the item for the cart
        const item: ShoppingCartItem = {
          ...(retailerId ? { retailerId: parseInt(retailerId, 10) } : {}),
          quantity,
          ...(retailerSku ? { retailerSku } : {}),
          ...(stacklineSku ? { stacklineSku } : {}),
        } as ShoppingCartItem;

        trackAction(AnalyticsTrackingEvent.BC_USER_ADDED_TO_BAG_FROM_URL, {
          retailerId: retailerId ? parseInt(retailerId, 10) : 0, // API requires retailerId, use 0 for stacklineSku
          items: [item],
        });

        // Add item to cart
        const checkoutIntent = await getCheckoutIntent({
          checkoutItems: [item],
        });
        const checkoutIntentId = checkoutIntent?.checkoutIntentId;
        // Remove the parameters from the URL before refreshing cart
        // This ensures we don't get into a loading state loop
        const newUrl = checkoutIntentId 
          ? `${window.location.pathname}?checkoutIntentId=${checkoutIntentId}` 
          : window.location.pathname;
        navigate(newUrl, { replace: true });

        // Refresh the cart contents
        await dispatch(getActiveCarts());
      } catch (error) {
        console.error("Failed to add item to bag:", error);
        // Remove parameters even on error to prevent stuck state
        const newUrl = window.location.pathname;
        navigate(newUrl, { replace: true });
      } finally {
        processingRef.current = false;
        setProcessing(false);
      }
    };

    addItemToBag();
  }, [searchParams, dispatch, navigate, trackAction]);

  return {
    retailerId: searchParams.get("retailerId"),
    retailerSku: searchParams.get("retailerSku"),
    stacklineSku: searchParams.get("stacklineSku"),
    quantity: searchParams.get("quantity"),
    processing,
  };
};
